import { useState } from "react";

import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerClose,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import ErrorMessage from "common/components/ui/ErrorMessage";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useLinkedInPost from "../../../../datahooks/useLinkedInPost";
import LinkedInAccountFormField from "../LinkedInAccountFormField";
import PostFormField from "./PostFormField";
import SelectedPost from "./SelectedPost";

import type { PostLeadListSource } from "../../../../types";

import PostImage from "assets/images/sources/post.png";

interface LinkedInPostProps {
  accountId: string;
  setAccountId: (accountId: string) => void;
  createSource: (settings: PostLeadListSource["settings"]) => void;
  isCreatingSource: boolean;
  goBack: () => void;
}

export default function LinkedInPost({
  accountId,
  setAccountId,
  createSource,
  isCreatingSource,
  goBack,
}: LinkedInPostProps) {
  const isDesktop = useTwBreakpoint("lg");

  const [postId, setPostId] = useState<string>();
  const [targetingParameters, setTargetingParameters] = useState<
    Omit<PostLeadListSource["settings"], "post_id" | "type">
  >({ comments: true, likes: true, reposts: true });

  const { linkedInPost, linkedInPostError, isLoadingLinkedInPost } =
    useLinkedInPost(accountId, postId);

  function addLeads() {
    createSource({
      type: "post",
      post_id: postId,
      ...targetingParameters,
    });
  }

  const isShowingSelectedPost = postId && !linkedInPostError;

  const Close = isDesktop ? DialogClose : DrawerClose;
  const Header = isDesktop ? DialogHeader : DrawerHeader;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;

  return (
    <>
      <Header
        title="Post Engagement"
        onBack={isShowingSelectedPost ? () => setPostId(undefined) : goBack}
      />
      <Body className="gap-y-3 lg:gap-y-6">
        {isShowingSelectedPost ? (
          <SelectedPost
            post={linkedInPost}
            isLoading={isLoadingLinkedInPost}
            targetingParameters={targetingParameters}
            setTargetingParameters={setTargetingParameters}
          />
        ) : (
          <>
            <div className="flex gap-3 lg:gap-5 max-lg:order-3 flex-col lg:flex-row-reverse">
              <LinkedInAccountFormField
                accountId={accountId}
                setAccountId={setAccountId}
              />
              <PostFormField setPostId={setPostId} />
            </div>
            <img src={PostImage} alt="" height={238} />
            <ErrorMessage
              className="mt-1.5 max-lg:order-4"
              error={linkedInPostError}
              fallbackErrorMessage="There was an error loading your post, please try again later"
              size="sm"
            />
          </>
        )}
      </Body>

      {isShowingSelectedPost && (
        <Footer className="justify-between">
          <Close asChild>
            <Button variant="secondary-black">Cancel</Button>
          </Close>
          <Button
            onClick={addLeads}
            disabled={!postId}
            isLoading={isCreatingSource}
          >
            Add leads
          </Button>
        </Footer>
      )}
    </>
  );
}
